/**
 * Le repos, son absence, et son prix — ⟦A-14 §2⟧, amendé par ⟦A-18⟧.
 *
 * Deux règles jumelles, qui n'ont de sens qu'ensemble :
 *
 * 1. **Le dépassement coûte de la santé mentale, à prix PLAT.** Chaque heure au-delà des 16 h
 *    d'une journée coûte `SM_DEPASSEMENT_PAR_HEURE` ⟦A-18⟧ — *« le dépassement d'heures, c'est
 *    de la fatigue : ça se répète chaque jour, donc ça se plafonne »*. La progression sans
 *    plafond que voulait Mathieu vit du côté de la **santé mentale** (mauvaises nuits
 *    enchaînées, flops), pas ici.
 * 2. **Une action assez longue CONTIENT ses nuits — mais ce sont de mauvaises nuits.** Elle
 *    remet le compteur de veille à zéro (donc aucune pénalité de dépassement) et rend
 *    `SM_NUIT_CONTENUE` au lieu de `SM_NUIT`.
 *
 * **Ce que la règle 2 répare** (décision de Mathieu du 15/08). Le dépassement se mesure aux
 * heures écoulées **depuis le dernier repos**, jamais à la durée de la tâche : appliqué
 * bêtement à la longueur d'une action, une longue de 48 h coûterait −1 176 de santé mentale.
 * Ce qui punit, c'est d'**enchaîner des actions sans dormir**, jamais qu'une tâche soit
 * longue. Sans la règle 2, un joueur qui produit quatre jours d'affilée se prendrait quatre
 * nuits blanches alors qu'il a évidemment dormi pendant son tournage.
 *
 * **Et l'effet recherché est une extinction lente, pas un coup.** Enchaîner ne coûte pas plus
 * cher : ça empêche de récupérer. Deux evergreens d'affilée, c'est huit nuits à +3 au lieu de
 * +10 — on descend doucement, sans qu'aucune action ne frappe, et le joueur regarde sa jauge
 * baisser. Le burn-out vient de l'**absence de repos**, pas d'un malus.
 *
 * ── L'état neutre par défaut (CLAUDE.md, ⟦A-10⟧) ────────────────────────────────
 *
 * `coutDepassement` vaut **0** tant qu'on reste dans ses `H_EVEILLE_PAR_JOUR` : un joueur qui dort quand il
 * faut ne voit jamais cette mécanique, et **aucune ancre de calibrage ne bouge** (elles
 * n'appellent que `computeVideoOutcome`, qui ignore tout du repos). `counters.last_rest_h`
 * est **optionnel à dessein** : `undefined` ⇔ `last_sleep_h` ⇔ le comportement d'avant
 * l'avenant, donc aucun état sérialisé à migrer.
 *
 * ── Pourquoi un compteur SÉPARÉ de `last_sleep_h` ──────────────────────────────
 *
 * ⚠️ Le piège, et il aurait été silencieux. `last_sleep_h` porte déjà un sens : c'est le
 * `Δh` de la **résolution de nuit** (§2.4) — rente evergreen et désabonnements sont versés
 * *pro rata* de cet intervalle. Le remettre à zéro au milieu d'une longue de 48 h ferait
 * **disparaître 48 h de rente et de désabonnements** sans que personne ne le voie. Le repos
 * a donc son propre compteur ; `last_sleep_h` garde son sens exact, et les 48 h se règlent
 * normalement au sommeil suivant. Même raison pour `subs_at_last_sleep`, référence de
 * l'anti-frustration (§10.4) : on n'y touche pas.
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucune horloge système, aucun `Math.random`.
 */
import { H_EVEILLE_PAR_JOUR, H_JOUR } from "./clock.js";
// ─────────────────────────────────────────────────────────────────────────────
// Constantes normatives
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦A-18⟧ **Coût PLAT** d'une heure de dépassement, en points de santé mentale. Chaque heure
 * au-delà des 16 h coûte le même prix — aucune progression.
 *
 * **Ce que ça remplace, et pourquoi.** ⟦A-14 §2⟧ posait une progression −1, −2, −3… avec le
 * pas marqué « ✎ à confirmer en playtest ». Le playtest a répondu : la somme est
 * *triangulaire*, donc elle explose dès qu'une action fait sauter plusieurs heures d'un coup.
 * Mesuré avec un short à 6 h, en partant de SM 90 et sans jamais dormir :
 *
 * | action | heures debout | AVANT (progressif) | APRÈS ⟦A-18⟧ (plat) |
 * |---|---|---|---|
 * | 3e short | 18 h | −3 | **−8** |
 * | 4e short | 24 h | −33 | **−24** |
 * | 5e short | 30 h | **−69 ⇒ burn-out** | **−24** |
 * | 6e short | 36 h | −105 | **−24** |
 *
 * Mots de Mathieu : *« la fatigue, pour moi, c'est la fatigue. Mais la santé mentale, c'est
 * le cumul — d'avoir fait plein de nuits pourries et des mauvaises vidéos. »* Le dépassement
 * se répète chaque jour, donc il se **plafonne** ; le cumul reste progressif et sans plafond,
 * et **il existe déjà** : `SM_NUIT_CONTENUE` (3 au lieu de 10) et le flop (−5, −8 au
 * deuxième d'affilée). La progression sans plafond est là, du bon côté.
 *
 * Effet recherché : trois actions dans la journée passent, la quatrième coûte cher et **se
 * paie**, la cinquième coûte cher aussi mais **ne tue pas d'un coup**. Le dépassement
 * redevient un arbitrage au lieu d'une faute.
 */
export const SM_DEPASSEMENT_PAR_HEURE = 4;
/**
 * Santé mentale rendue par une nuit **contenue** dans une action longue ✎ — contre
 * `SM_NUIT = 10` pour une vraie nuit (`actions.ts`).
 *
 * C'est **tout le levier** de la règle : 3 au lieu de 10, donc produire quatre jours d'affilée
 * ne punit pas, ça empêche seulement de remonter. Le télégraphiage est gratuit — la jauge
 * descend sous les yeux du joueur, aucun message d'alerte n'est nécessaire pour qu'il le voie.
 */
export const SM_NUIT_CONTENUE = 3; // ✎
// ─────────────────────────────────────────────────────────────────────────────
// Lectures — toutes pures, toutes utilisables en preview par l'UI
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Instant du dernier repos, en heures éveillées : une vraie nuit **ou** une nuit contenue
 * dans une action longue. `undefined` ⇔ `last_sleep_h` (état neutre, voir l'en-tête).
 */
export function dernierRepos(state) {
    return state.counters.last_rest_h ?? state.counters.last_sleep_h;
}
/** Heures éveillées écoulées depuis le dernier repos, à `clockH` (défaut : maintenant). */
export function heuresDepuisRepos(state, clockH = state.clock_h) {
    return Math.max(0, clockH - dernierRepos(state));
}
/**
 * Heures **au-delà du budget de travail** d'une journée (`H_EVEILLE_PAR_JOUR`), à `clockH`.
 * 0 tant qu'on tient sa journée.
 *
 * ⚠️ ⟦A-19⟧ **`H_EVEILLE_PAR_JOUR`, surtout pas `H_JOUR`.** Depuis que la journée fait 24 h
 * (16 h de travail + 8 h de nuit), les deux ont divergé. Facturer le dépassement au-delà de
 * 24 h supprimerait la fatigue purement et simplement : personne ne tient 24 h de travail
 * d'affilée, donc plus aucune action ne franchirait jamais le seuil.
 */
export function heuresDeDepassement(state, clockH = state.clock_h) {
    return Math.max(0, heuresDepuisRepos(state, clockH) - H_EVEILLE_PAR_JOUR);
}
/**
 * Coût cumulé de `n` heures de dépassement — ⟦A-18⟧ : **linéaire**, `n × 4`, en points de
 * santé mentale (valeur **positive** = à retrancher).
 *
 * ⚠️ La linéarité n'est pas qu'un réglage : c'est elle qui rend le coût **incrémental** de
 * `effetRepos` indépendant du découpage. Avec la somme triangulaire, deux shorts de 6 h
 * coûtaient plus cher qu'une action de 12 h à heures égales ; à prix plat, une heure de trop
 * vaut le même prix quelle que soit l'action qui la fait franchir.
 */
export function coutCumuleDepassement(n) {
    if (n <= 0)
        return 0;
    return SM_DEPASSEMENT_PAR_HEURE * n;
}
/**
 * Nombre de nuits **contenues** par une action de `hours` heures : une par jour simulé entier
 * traversé. Une action plus courte qu'une journée n'en contient aucune — et c'est bien elle
 * qui peut faire dépasser.
 *
 * ⟦A-19⟧ Ici c'est bien `H_JOUR` (le calendrier) : on compte des NUITS, pas des heures de
 * travail. Une longue de 48 h en contient donc 2 (elle en contenait 3 quand la journée
 * faisait 16 h) — le changement est voulu, il est signalé au rapport.
 */
export function nuitsContenues(hours) {
    return Math.floor(Math.max(0, hours) / H_JOUR);
}
/**
 * Une action **repose-t-elle** le joueur ? Deux familles, et une exception :
 *
 *  - toute action qui contient au moins une nuit (≥ `H_JOUR`) : on ne traverse pas un jour simulé
 *    sans dormir, même mal ;
 *  - `sleep`, évidemment — mais elle pose elle-même ses compteurs dans la file §2.3.
 *
 * L'exception est `pause` : ses `H_SEMAINE` **reposent** (donc elles remettent le compteur à zéro,
 * sinon on facturerait une semaine de nuit blanche) mais elles ne rendent PAS de SM à ce
 * titre — la table §2.2 lui en donne déjà 40, et empiler 7 × 3 par-dessus gonflerait en
 * silence une valeur calibrée. Voir `smDesNuitsContenues`.
 */
export function actionRepose(action, hours) {
    return action.type === "sleep" || nuitsContenues(hours) >= 1;
}
/**
 * Santé mentale rendue par les nuits contenues d'une action. 0 pour `sleep` (la table §2.2
 * porte déjà `SM_NUIT`) et 0 pour `pause` (voir `actionRepose`).
 */
export function smDesNuitsContenues(action, hours) {
    if (action.type === "sleep" || action.type === "pause")
        return 0;
    return nuitsContenues(hours) * SM_NUIT_CONTENUE;
}
/**
 * **Le cœur de la règle.** Ce qu'une action de `hours` heures fait au repos, à partir de
 * l'état d'**avant** l'action — donc calculable en preview par le client, ce qui rend la
 * mécanique *prévenante* plutôt que *surprenante* (PLAN : l'échec est toujours télégraphié).
 *
 * ⚠️ **Le coût est INCRÉMENTAL** : `T(après) − T(avant)`, jamais `T(après)`. Les heures de
 * dépassement déjà accumulées ont déjà été facturées par les tours précédents ; les refacturer
 * ferait payer un joueur immobile.
 */
export function effetRepos(state, action, hours) {
    const depassementAvant = heuresDeDepassement(state);
    const repose = actionRepose(action, hours);
    const depassementApres = repose ? 0 : heuresDeDepassement(state, state.clock_h + hours);
    return {
        cout_depassement: repose
            ? 0
            : coutCumuleDepassement(depassementApres) - coutCumuleDepassement(depassementAvant),
        sm_nuits_contenues: smDesNuitsContenues(action, hours),
        nuits_contenues: nuitsContenues(hours),
        depassement_avant: depassementAvant,
        depassement_apres: depassementApres,
        repose,
    };
}
/**
 * **Le versant « prévenir » de la règle — et il n'est pas décoratif.**
 *
 * Le PLAN pose que l'échec est **toujours télégraphié et évitable** ; la table de
 * `SM_DEPASSEMENT_PAR_HEURE` montre qu'un 4e short coûte encore 24 points d'un coup.
 * Une mécanique qui surprend est ratée même si elle est juste : le client DOIT pouvoir
 * afficher le prix avant le clic. Cette fonction est ce contrat — pure, sans effet de bord,
 * appelable sur l'état d'avant l'action.
 *
 * `hours` est la durée **effective** de l'action, `actionCost(action, state, mods).hours` —
 * elle est passée plutôt que recalculée pour que le coût prévisualisé soit exactement celui
 * qui sera débité, `action_cost_mult` compris (le précédent est ⟦A-11 §1⟧ : valider un coût
 * que la file ne débitera pas est un mensonge).
 */
export function previsionRepos(state, action, hours) {
    return effetRepos(state, action, hours);
}
/**
 * Seuil au-delà duquel la perte mérite une alerte à l'écran ✎. En dessous, la jauge qui
 * bouge suffit à raconter l'histoire — on ne veut pas d'un jeu qui crie.
 */
export const SM_DEPASSEMENT_ALERTE = 5; // ✎
/**
 * Applique l'effet au clone de travail de la file §2.3 : SM, puis le compteur de repos.
 * MUTE `state`. À appeler **après** l'avancée de `clock_h` (pas 4), pour que
 * `last_rest_h` porte l'instant où l'action se termine.
 *
 * `sleep` est volontairement laissée à la file (§2.3 pas 6, `actions.ts`), qui pose déjà
 * `last_sleep_h` : on synchronise seulement `last_rest_h` sur lui, sans dupliquer la règle.
 */
export function appliquerRepos(state, effet) {
    state.mental -= effet.cout_depassement;
    state.mental += effet.sm_nuits_contenues;
    if (effet.repose)
        state.counters.last_rest_h = state.clock_h;
}
//# sourceMappingURL=repos.js.map